import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { ShieldCheck, BadgeCheck, X, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { EndorsementBadge } from './EndorsementBadge';

/**
 * EndorsementMenu — combined badge + admin-action dropdown.
 *
 *   • Non-admins: renders only the EndorsementBadge (no controls).
 *   • Admins / tenant_admins: can promote + clear.
 *   • Super admins: can also certify (the higher trust tier).
 *
 * Props:
 *   contentType: 'dashboard' | 'report' | 'semantic_model' | 'visual'
 *   contentId:   the canonical id for that type
 *   endorsement: current value (shape from server)
 *   onChange:    (newEndorsement) => void — called after a successful PUT
 *   compact:     bool   — pulls the icon-only badge form for tight rows
 */
export function EndorsementMenu({ contentType, contentId, endorsement, onChange, compact = false }) {
  const { user } = useAuth();
  const role = user?.role || '';
  const isAdmin = role === 'admin' || role === 'tenant_admin' || role === 'super_admin';
  const isSuperAdmin = role === 'super_admin';
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const level = endorsement?.level || 'none';

  // Non-admin viewers see the badge with no controls. When there's no
  // badge to show either, render nothing — never want an empty hover pill.
  if (!isAdmin) {
    return level === 'none' ? null : <EndorsementBadge endorsement={endorsement} size={compact ? 'sm' : 'md'} showLabel={!compact} />;
  }

  const setLevel = async (newLevel) => {
    setSaving(true);
    try {
      const res = await api.setEndorsement(contentType, contentId, newLevel);
      onChange?.({ level: res.level, set_by_email: res.set_by_email, set_at: res.set_at, note: res.note });
      toast.success(newLevel === 'none' ? 'Endorsement cleared' : `Marked as ${newLevel}`);
      setOpen(false);
    } catch (e) {
      toast.error(e.message || 'Endorsement change failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        disabled={saving}
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[9px] font-medium transition-colors hover:opacity-90 ${
          level === 'certified' ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' :
          level === 'promoted'  ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'       :
                                  'bg-muted/30 text-muted-foreground/80 border-border/40'
        }`}
        title="Set endorsement"
        data-testid={`endorse-${contentType}-${contentId}`}
      >
        {level === 'certified' && <BadgeCheck className="w-2.5 h-2.5" strokeWidth={2.5} />}
        {level === 'promoted'  && <ShieldCheck className="w-2.5 h-2.5" strokeWidth={2.5} />}
        {level === 'none' && <span className="opacity-60">Endorse</span>}
        {level !== 'none' && (compact ? null : <span className="uppercase tracking-wider">{level}</span>)}
        <ChevronDown className="w-2.5 h-2.5 opacity-70" />
      </button>
      {open && (
        <>
          {/* Backdrop to dismiss on outside click */}
          <div className="fixed inset-0 z-[199]" onClick={() => setOpen(false)} />
          <div
            className="absolute z-[200] top-full mt-1 right-0 min-w-44 rounded-xl border border-border/40 shadow-2xl overflow-hidden"
            style={{ background: 'var(--glass-bg, rgba(15,20,40,0.96))', backdropFilter: 'blur(16px)' }}
            data-testid={`endorse-menu-${contentId}`}
          >
            <button
              onClick={() => setLevel('certified')}
              disabled={!isSuperAdmin || saving || level === 'certified'}
              className="w-full flex items-start gap-2 px-3 py-2 text-left text-xs hover:bg-emerald-500/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid="endorse-certified-btn"
            >
              <BadgeCheck className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-semibold text-emerald-300">Certified</div>
                <div className="text-[10px] text-muted-foreground leading-tight">
                  {isSuperAdmin ? 'Independently verified — super-admin only' : 'Super-admin only'}
                </div>
              </div>
            </button>
            <button
              onClick={() => setLevel('promoted')}
              disabled={saving || level === 'promoted'}
              className="w-full flex items-start gap-2 px-3 py-2 text-left text-xs hover:bg-amber-500/10 disabled:opacity-40 transition-colors"
              data-testid="endorse-promoted-btn"
            >
              <ShieldCheck className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-semibold text-amber-300">Promoted</div>
                <div className="text-[10px] text-muted-foreground leading-tight">Owners recommend for sharing</div>
              </div>
            </button>
            {level !== 'none' && (
              <button
                onClick={() => setLevel('none')}
                disabled={saving}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs text-muted-foreground hover:bg-muted/30 border-t border-border/30 transition-colors"
                data-testid="endorse-clear-btn"
              >
                <X className="w-3.5 h-3.5" />
                Clear endorsement
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default EndorsementMenu;
